/**
 * Alarm tone catalogue — the small set of built-in siren patterns the user can
 * pick between for each alarm.
 *
 * Everything is synthesised with the Web Audio API (see alarmSound.ts) rather
 * than shipping audio files, so there's no asset to bundle or fail to load and a
 * platform with no audio is a clean no-op. A tone is just a short sequence of
 * shaped notes; the runner plays whichever tone an alarm is set to, at that
 * alarm's chosen volume, and repeats it while the alarm stands.
 *
 * Why a fixed catalogue rather than free choice: a skipper needs to be able to
 * tell alarms apart by ear without looking at the screen, so the set is a handful
 * of clearly-distinct patterns (a rising sweep sounds nothing like a gentle
 * chime), and the loud, urgent ones are reserved as the defaults for the alarms
 * that actually put the boat or crew in danger.
 */

/** The waveform shapes we use. Square/sawtooth cut through wind; sine is gentle. */
export type ToneWave = 'square' | 'sine' | 'sawtooth' | 'triangle';

/** One shaped note in a tone: when it starts, how long, its pitch and shape. */
export interface ToneNote {
  /** Start frequency, Hz. */
  freq: number;
  /** Start offset from the top of the burst, seconds. */
  at: number;
  /** Length, seconds. */
  dur: number;
  /** Waveform; defaults to square (loud and cutting) when omitted. */
  wave?: ToneWave;
  /** When set, glide from freq to this over the note (a sweep/siren slide). */
  toFreq?: number;
}

/**
 * The id of a built-in tone.
 *
 * The first five are the SEMANTIC tones, one per alarm category, tuned so a
 * skipper can learn "that's the depth one" or "that's AIS" by ear without looking
 * at the screen (see alarmCategories.ts for which alarm maps to which). They sit
 * at deliberately different pitch registers, note counts and timbres so no two
 * are easily confused. The rest are the older generic patterns, kept so a skipper
 * who prefers one can still pick it and so a config saved by an earlier build
 * still points at a real tone.
 */
export type ToneId =
  | 'danger'
  | 'collision'
  | 'gnss'
  | 'anchor'
  | 'notice'
  | 'siren'
  | 'warble'
  | 'sweep'
  | 'pulse'
  | 'triple'
  | 'chime';

/** A tone: its id, a short label/blurb for the picker, and the notes to play. */
export interface ToneDef {
  id: ToneId;
  label: string;
  blurb: string;
  notes: ToneNote[];
}

/**
 * The catalogue, in picker order. The first five are the SEMANTIC tones, one per
 * alarm category, and they're the defaults the app ships. They're built to be
 * told apart by ear: a low firm triple (danger) sounds nothing like a high sonar
 * two-tone (collision) or a soft high chirp (a GNSS caution). The remaining
 * entries are the older generic patterns, kept so a skipper can still choose one.
 */
export const ALARM_TONES: ToneDef[] = [
  {
    // DEPTH / GROUNDING / SAFETY-CONTOUR. Low and firm, a serious triple pulse
    // that reads as "you're standing into danger" rather than a shrill panic. Low
    // pitch (300 Hz) so it feels like a warning horn, square so it still carries.
    id: 'danger',
    label: 'Danger',
    blurb: 'low firm triple pulse',
    notes: [
      { freq: 300, at: 0, dur: 0.13, wave: 'square' },
      { freq: 300, at: 0.19, dur: 0.13, wave: 'square' },
      { freq: 300, at: 0.38, dur: 0.16, wave: 'square' },
    ],
  },
  {
    // AIS COLLISION / CPA. A "sonar-ish" two-tone, higher than the danger horn and
    // rounder (triangle), high-then-low, so traffic reads as its own distinct
    // sound and never blurs into the depth alarm.
    id: 'collision',
    label: 'Collision',
    blurb: 'sonar-style two-tone',
    notes: [
      { freq: 1050, at: 0, dur: 0.16, wave: 'triangle' },
      { freq: 780, at: 0.21, dur: 0.2, wave: 'triangle' },
    ],
  },
  {
    // GNSS INTEGRITY (lost fix / poor accuracy / possible spoofing). A caution, not
    // an imminent grounding, so it's softer and higher: three quick gentle chirps
    // up top (sine, ~1320 Hz) that catch the ear without sounding like the boat's
    // about to hit something.
    id: 'gnss',
    label: 'Caution',
    blurb: 'soft high triple chirp',
    notes: [
      { freq: 1320, at: 0, dur: 0.07, wave: 'sine' },
      { freq: 1320, at: 0.13, dur: 0.07, wave: 'sine' },
      { freq: 1320, at: 0.26, dur: 0.07, wave: 'sine' },
    ],
  },
  {
    // ANCHOR DRAG. An insistent mid-tone double (660 Hz, square) that nags: it's
    // pitched between the low danger horn and the high GNSS chirp, so it's clearly
    // its own sound, and the repeat cadence keeps it pestering until acknowledged.
    id: 'anchor',
    label: 'Anchor',
    blurb: 'insistent mid double',
    notes: [
      { freq: 660, at: 0, dur: 0.15, wave: 'square' },
      { freq: 660, at: 0.23, dur: 0.15, wave: 'square' },
    ],
  },
  {
    // LOW BATTERY / TIDE HEIGHT / other low-priority notices. A single gentle blip
    // (520 Hz, sine): a quiet nudge that doesn't read as an emergency.
    id: 'notice',
    label: 'Notice',
    blurb: 'gentle single blip',
    notes: [{ freq: 520, at: 0, dur: 0.18, wave: 'sine' }],
  },
  {
    id: 'siren',
    label: 'Siren',
    blurb: 'two-tone, high then low',
    notes: [
      { freq: 880, at: 0, dur: 0.18, wave: 'square' },
      { freq: 620, at: 0.2, dur: 0.2, wave: 'square' },
    ],
  },
  {
    id: 'warble',
    label: 'Warble',
    blurb: 'fast alternating, urgent',
    notes: [
      { freq: 1200, at: 0, dur: 0.1, wave: 'square' },
      { freq: 1650, at: 0.12, dur: 0.1, wave: 'square' },
      { freq: 1200, at: 0.24, dur: 0.1, wave: 'square' },
      { freq: 1650, at: 0.36, dur: 0.1, wave: 'square' },
    ],
  },
  {
    id: 'sweep',
    label: 'Sweep',
    blurb: 'rising slide, attention-grabbing',
    notes: [{ freq: 500, at: 0, dur: 0.4, wave: 'sawtooth', toFreq: 1500 }],
  },
  {
    id: 'pulse',
    label: 'Pulse',
    blurb: 'single firm beep',
    notes: [{ freq: 800, at: 0, dur: 0.25, wave: 'square' }],
  },
  {
    id: 'triple',
    label: 'Triple',
    blurb: 'three quick chirps',
    notes: [
      { freq: 1000, at: 0, dur: 0.09, wave: 'square' },
      { freq: 1000, at: 0.14, dur: 0.09, wave: 'square' },
      { freq: 1000, at: 0.28, dur: 0.09, wave: 'square' },
    ],
  },
  {
    id: 'chime',
    label: 'Chime',
    blurb: 'gentle two-note, low priority',
    notes: [
      { freq: 660, at: 0, dur: 0.22, wave: 'sine' },
      { freq: 440, at: 0.24, dur: 0.3, wave: 'sine' },
    ],
  },
];

const TONE_BY_ID: Record<ToneId, ToneDef> = ALARM_TONES.reduce(
  (acc, t) => {
    acc[t.id] = t;
    return acc;
  },
  {} as Record<ToneId, ToneDef>,
);

/** The tone for an id, falling back to the siren if an old saved id is unknown. */
export function toneById(id: ToneId): ToneDef {
  return TONE_BY_ID[id] ?? TONE_BY_ID.siren;
}

/** The next tone id in catalogue order (wraps), for a tap-to-cycle picker. */
export function nextToneId(id: ToneId): ToneId {
  const i = ALARM_TONES.findIndex((t) => t.id === id);
  const next = ALARM_TONES[(i + 1) % ALARM_TONES.length];
  return next.id;
}

/**
 * The MOB (man overboard) confirmation. This is NOT an alarm and NOT in the
 * per-alarm picker: it's the sound the crew hear the instant a MOB is dropped, to
 * confirm the mark went down even in chaos. So it's built to feel like a firm,
 * positive "got it" rather than a warning: a rising three-note arpeggio
 * (C5-E5-G5, square so it cuts through wind), climbing rather than urgent. It
 * plays once, at full volume, over the top of any standing alarm (see
 * playMobConfirmation in alarmSound.ts), and is paired with the mob haptic.
 *
 * The notes strictly rise in pitch, which is what makes it read as a confirmation
 * and not a siren; that rise is asserted in the tests.
 */
export const MOB_CONFIRM_TONE: ToneDef = {
  id: 'notice', // id is unused for MOB (it's played directly), reuse a valid one.
  label: 'MOB confirmed',
  blurb: 'rising three-note, got it',
  notes: [
    { freq: 523, at: 0, dur: 0.12, wave: 'square' },
    { freq: 659, at: 0.13, dur: 0.12, wave: 'square' },
    { freq: 784, at: 0.26, dur: 0.22, wave: 'square' },
  ],
};
